import React, { useRef, useEffect, useCallback } from 'react';
import 'react-responsive-modal/styles.css';

import { useLocation, useNavigate } from 'react-router-dom';

// eslint-disable-next-line import/no-unresolved
import Bg1 from '../../assets/images/cityscape.png.png';
import Caring from '../../assets/svgs/alumni3.svg';
import Caring1 from '../../assets/images/caring1.png';
import Caring2 from '../../assets/images/caring2.png';

import schools from '../../data/schools';
import logos from '../../data/logos';
import benefits from '../../data/benefits';

import Button from '../../components/inputs/Button';
import Alumni from '../../components/layouts/Alumni';
import PictureScroll from '../../components/layouts/PictureScroll';
import ContactUs from '../../components/layouts/ContactUs';
import Testimonial from '../../components/layouts/Testimonial';
import Team from '../../components/layouts/Team';
import GoverningCouncil from '../../components/layouts/GoverningCouncil';
import SmeSchoolPopup from '../../components/layouts/SmeSchoolPopup';

function Homepage() {
  const { hash } = useLocation();
  const navigate = useNavigate();

  const topSectionRef = useRef();
  const schoolsSectionRef = useRef();
  const contactusSectionRef = useRef();
  const benefitsScrollRef = useRef(null);

  const startAutoScroll = useCallback(() => {
    const el = benefitsScrollRef.current;
    if (!el) return undefined;

    let rafId;
    let direction = 1;
    let paused = false;

    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    el.addEventListener('mouseenter', onEnter);
    el.addEventListener('mouseleave', onLeave);

    const tick = () => {
      if (!paused) {
        const max = el.scrollWidth - el.clientWidth;
        if (max > 0) {
          el.scrollLeft += direction * 2;
          if (el.scrollLeft >= max) direction = -1;
          else if (el.scrollLeft <= 0) direction = 1;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('mouseenter', onEnter);
      el.removeEventListener('mouseleave', onLeave);
    };
  }, []);

  useEffect(() => {
    const cleanup = startAutoScroll();
    return cleanup;
  }, [startAutoScroll]);

  const scrollToSection = () => {
    if (hash === '#top') {
      topSectionRef?.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (hash === '#schools') {
      schoolsSectionRef?.current?.scrollIntoView({ behavior: 'smooth' });
    } else if (hash === '#contact-us') {
      contactusSectionRef?.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    document.title = 'Purple Academy - Home';
    scrollToSection();
  }, []);

  scrollToSection();

  return (
    <div>
      {/* SME Business School 2026 application popup (shown once per 24h) */}
      <SmeSchoolPopup />

      <section
        ref={topSectionRef}
        style={{ background: `url(${Bg1}) center no-repeat` }}
        className="relative w-full min-h-screen flex items-center justify-center !bg-cover"
      >
        <div className="content w-full px-6">
          <center className="max-w-4xl mx-auto">

            <h1 className="font-semibold text-4xl md:text-6xl mb-3 font-title sm:tracking-tighter">
              Your journey to excellence
            </h1>
            <h1 className="font-semibold text-4xl md:text-6xl mb-7 text-primary font-title sm:tracking-tighter">
              starts here
            </h1>
            <p className="big-p max-w-3xl">
              The Academy invests in you so you can succeed in your role and
              develop an exciting, dynamic and rewarding career at Wema Bank.
            </p>

            <div className="flex justify-center mt-16">
              <Button
                fontSize="text-lg"
                paddingX="px-12"
                paddingY="py-3"
                onClick={() => navigate('/sign-in')}
              >
                Knight&apos;s Corner
              </Button>
            </div>
          </center>
        </div>
      </section>

      <section className="mb-28 sm:mb-52">
        <PictureScroll />
      </section>

      <section className="mb-28 sm:mb-52">
        <div className="brand-wheel">
          <div className="brand-slide">
            {logos.map((item) => (
              <div
                className={`logo-card items-center ${item.w} ${item.h}`}
                key={item.logo}
              >
                <img
                  src={item.logo}
                  alt={item.alt}
                  className={`${item.w} ${item.h}`}
                />
              </div>
            ))}
          </div>
          <div className="brand-slide delay">
            {logos.map((item) => (
              <div
                className={`logo-card items-center ${item.w} ${item.h}`}
                key={item.logo}
              >
                <img
                  src={item.logo}
                  alt={item.alt}
                  className={`${item.w} ${item.h}`}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative w-full mb-16 sm:mb-32 overflow-hidden">
        <div className="bg-[#0f0a13] text-white py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
              <div className="max-w-xl">
                <p className="text-xs text-primary uppercase tracking-[0.3em] font-semibold mb-4">Why Purple Academy</p>
                <h1 className="font-black text-4xl md:text-6xl font-title leading-tight">
                  What&apos;s in it
                  <br />
                  <span className="text-primary">for you?</span>
                </h1>
              </div>
              <div className="flex-shrink-0 text-right hidden md:block">
                <p className="text-[120px] font-black font-title leading-none text-white/5 select-none">4</p>
                <p className="text-sm text-white/40 -mt-6 uppercase tracking-widest">Core Pillars</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mb-16 border-t border-white/10 pt-10">
              <p className="text-white/70 text-lg leading-8">
                Purple Academy is designed to equip you with the
                {' '}
                <span className="text-white font-semibold">knowledge, skills, and capabilities</span>
                {' '}
                required to excel in your current role and advance your professional growth.
                Through comprehensive onboarding programmes, open and in-plant courses,
                immersive learning simulations, team-building sessions,
                and tailored development interventions.
              </p>
              <p className="text-white/70 text-lg leading-8">
                We provide learning experiences that are
                {' '}
                <span className="text-primary font-semibold">practical, impactful, and aligned</span>
                {' '}
                with your career aspirations — intentionally designed to
                {' '}
                <span className="text-white font-semibold">empower individuals, strengthen teams, and drive organisational success</span>
                , enabling everyone across the Bank to learn, grow, and thrive together.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {benefits.map((item) => (
                <div
                  key={item.sn}
                  className="group relative bg-white/5 border border-white/10 rounded-2xl p-6 hover:bg-primary/20 hover:border-primary/40 transition-all duration-300 cursor-default overflow-hidden"
                >
                  <p className="text-5xl font-black font-title text-white/10 group-hover:text-primary/30 transition-colors duration-300 mb-4 leading-none">{item.sn}</p>
                  <img src={item.img} alt={item.title} className="w-12 h-12 mb-4 opacity-80 group-hover:opacity-100 transition-opacity duration-300" />
                  <h3 className="font-bold font-title text-white text-base leading-snug mb-2">{item.title}</h3>
                  <p className="text-white/50 text-sm leading-relaxed group-hover:text-white/70 transition-colors duration-300">{item.text}</p>
                  <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-primary scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left rounded-b-2xl" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="relative w-full mb-28 sm:mb-52">
        <div className="content mb-8 sm:mb-16">
          <h1 className="section-header text-center">Meet the Alumni</h1>
        </div>
        <Alumni />
      </section>

      <section
        ref={schoolsSectionRef}
        className="relative w-full mb-28 sm:mb-52"
      >
        <div className="content">
          <center className="max-w-4xl mx-auto mb-8 sm:mb-20">
            <h1 className="section-header">Our 5 key Schools</h1>
            <p className="big-p">
              The five key schools offer structured and flexible interventions
              that give employees the opportunity to chart their own unique
              course and define their career journeys.
            </p>
          </center>

          <div className="flex -mx-2 flex-wrap">
            {schools.map((item) => (
              <div key={item.title} className="w-full p-2 sm:w-1/2 md-2:w-1/3">
                <a href={item.link} className="w-full h-full">
                  <div className={`${item.bgColor} w-full h-full`}>
                    <img
                      src={item.img}
                      alt={item.title}
                      className="w-full h-auto"
                    />
                    <div className="text-white px-5 pt-4 pb-14">
                      <h3 className="font-title text-xl font-bold mb-5">
                        {item.title}
                      </h3>
                      <p className="">{item.desc}</p>
                    </div>
                  </div>
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative mb-28 sm:mb-52">
        <div className="content">
          <div className="flex flex-col md-2:flex-row items-center gap-12 md-2:gap-16">

            {/* Text side */}
            <div className="w-full md-2:w-[calc(100%-550px)] flex items-center">
              <div className="w-full text-center md-2:text-left">
                <p className="text-xs text-primary uppercase tracking-widest font-semibold mb-3">Community Impact</p>
                <h1 className="section-header mb-5">Caring and Sharing</h1>
                <p className="big-p mb-6 md:mb-8 text-textColor">
                  We partner with our sustainability team to improve learning
                  and drive sustainable growth among youth in society. Our
                  caring and sharing programmes spring up when we find a need
                  that we can address through learning, and our team is always
                  excited to build an impactful and engaging solution.
                </p>
                <div className="flex flex-wrap justify-center md-2:justify-start gap-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-lg">🎓</span>
                    </div>
                    <span className="text-sm font-medium text-textColor">Youth Education</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-lg">🌱</span>
                    </div>
                    <span className="text-sm font-medium text-textColor">Sustainable Growth</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-bold text-lg">🤝</span>
                    </div>
                    <span className="text-sm font-medium text-textColor">Community Building</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Image grid */}
            <div className="w-full sm:w-[550px] mx-auto flex-shrink-0">
              <div className="w-full h-[420px] sm:h-[520px] flex gap-3">

                {/* Main large image */}
                <div className="w-2/3 rounded-2xl relative overflow-hidden group shadow-lg">
                  <img
                    src={Caring1}
                    alt="Caring and Sharing"
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="w-6 h-[3px] bg-primary rounded-full mb-2" />
                    <h3 className="text-white font-title font-bold text-sm leading-tight">
                      Wesley Girls High School
                    </h3>
                    <p className="text-white/75 text-xs mt-0.5">
                      Design Thinking &amp; Value Creation Workshop
                    </p>
                  </div>
                </div>

                {/* Right column */}
                <div className="w-1/3 flex flex-col gap-3">
                  {/* Top decorative tile */}
                  <div className="flex-1 rounded-2xl bg-primary flex items-center justify-center overflow-hidden shadow-lg">
                    <img src={Caring} alt="Caring illustration" className="w-full h-full object-cover opacity-90" />
                  </div>
                  {/* Bottom photo */}
                  <div className="flex-1 rounded-2xl relative overflow-hidden group shadow-lg">
                    <img
                      src={Caring2}
                      alt="Caring and Sharing"
                      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      <Testimonial />
      <GoverningCouncil />
      <Team />

      <section ref={contactusSectionRef} className="relative w-full mb-32">
        <ContactUs />
      </section>
    </div>
  );
}

export default Homepage;
